import "./PayoffMatrixComponent.scss";
import {IComponentOptions} from "angular";
import {PayoffService} from "../service/PayoffService";
import "rxjs/add/operator/switchMap";
import "rxjs/add/operator/debounceTime";
import {ReplaySubject} from "rxjs/ReplaySubject";
import {PriceIncrementType, securityVolChange, TfPayoffRequest} from "../service/PayoffRequests";
import {Observable} from "rxjs/Observable";
import {TfPayoffResponse} from "../service/PayoffResponses";
import {PayoffMatrixGridData} from "./models/PayoffMatrixGridData";
import {daysBetween} from "../../../../js/util/DateTimeUtil";
import {ArrayUtil} from "../../../../js/util/ArrayUtil";
import {BehaviorSubject} from "rxjs";
import moment = require("moment");

export const PayoffMatrix = "tfPayoffMatrix";

export function payoffMatrix(): IComponentOptions {
    return {
        templateUrl: "app/components/charts/payoff/payoff-matrix/PayoffMatrixComponent.html",
        bindings: {
            optionTrades: "<",
            equityTrades: "<"
        },
        controllerAs: "ctrl",
        controller: PayoffMatrixComponentController
    };
}

export class PayoffMatrixComponentController {

    /** @input */
    public set optionTrades(trades: OptionTrades | null) {
        if (trades == null) {
            this.optionTrades$.next([]);
        } else {
            this.optionTrades$.next(trades);
        }
    }

    private readonly optionTrades$ = new ReplaySubject<OptionTrades>(1);

    /** @input */
    public set equityTrades(trades: EquityTrades | null) {
        if (trades == null) {
            this.equityTrades$.next([]);
        } else {
            this.equityTrades$.next(trades);
        }
    }

    private readonly equityTrades$ = new ReplaySubject<EquityTrades>(1);

    public readonly vol$ = new BehaviorSubject(0);
    public readonly priceIncrement$ = new BehaviorSubject(1);
    public readonly theoView$ = new BehaviorSubject(false);

    public readonly gridData$: Observable<PayoffMatrixGridData>;

    /** @ngInject */
    public constructor(private readonly payoffService: PayoffService) {
        this.gridData$ = this.buildGridData();
    }

    private buildGridData(): Observable<PayoffMatrixGridData> {
        const distinctVol$ = this.vol$.distinctUntilChanged();
        const distinctIncrement$ = this.priceIncrement$.distinctUntilChanged();
        const distinctTheo$ = this.theoView$.distinctUntilChanged();

        const dataSource$ = Observable.combineLatest(this.optionTrades$,
                                                     this.equityTrades$,
                                                     distinctVol$,
                                                     distinctIncrement$);

        const payoffData$ = dataSource$.debounceTime(100)
                                       .map(([options, equities, vol, increment]) => this.buildRequest(options,
                                                                                                       equities,
                                                                                                       vol,
                                                                                                       increment))
                                       .switchMap((req) => this.retrievePayoffData(req));

        const source$ = Observable.combineLatest(payoffData$, distinctTheo$);

        return source$.map(([data, showTheo]) => this.generateGridData(data, showTheo))
                      .publishReplay(1)
                      .refCount();
    }

    private buildRequest(options: OptionTrades,
                         equities: EquityTrades,
                         vol: number,
                         priceIncrement: number): TfPayoffRequest {
        const security = [...options, ...equities].map((t) => t.code)
                                                  .map((c) => c.substr(0, 3))[0];

        const scenario = securityVolChange(security, vol);

        return {
            trades: options,
            equityTrades: equities,
            scenarios: scenario,
            priceIncrement,
            priceIncrementType: PriceIncrementType.PERCENT
        };
    }

    private retrievePayoffData(request: TfPayoffRequest): Observable<TfPayoffResponse> {
        const response = this.payoffService.tfCalculatePayoff(request);
        return Observable.fromPromise(response)
                         .catch(() => Observable.empty());
    }

    /*
     * The Response data comes in a flattened format where the payoffData is a single Array of values
     * which belong to the combination of date and price steps, thus we need to figure out which index in the
     * payoffData array each one requires.
     * That is why there is a horrible double map involved which uses a lot of index values to grab the data.
     */
    private generateGridData(response: TfPayoffResponse, showTheo: boolean): PayoffMatrixGridData {
        const payoffData = showTheo ? response.theoChangeData : response.payOffData;
        const priceStepCount = response.priceSteps.length;
        const midpointIndex = (response.priceSteps.length - 1) / 2;

        const isPoints = response.points;
        const columns = this.columnHeaders(response.dateSteps);
        const rows = response.priceSteps.map((price, index) => {
            const payoffs = ArrayUtil.range(response.dateSteps.length)
                                     .map(d => d * priceStepCount)
                                     .map(d => d + index)
                                     .map(d => payoffData[d]!);
            return {
                price,
                payoffPoints: payoffs,
                midpoint: index === midpointIndex
            };
        });

        const orderedRows = rows.sort((r1, r2) => r2.price - r1.price);

        return {
            points: isPoints,
            columns,
            rows: orderedRows,
            rounding: (showTheo ? isPoints ? 1 : 3 : 0)
        };
    }

    private columnHeaders(dates: Array<string>): Array<string> {
        if (dates == null || dates.length === 0) {
            return [];
        }
        const now = moment(dates[0]);
        const expiry = moment(dates[dates.length - 1]);

        return dates.map(d => moment(d))
                    .map(d => {

                        const difference = daysBetween(d, now);
                        const roundedDifference = Math.round(difference);

                        if (roundedDifference === 0) {
                            return "Today";
                        } else if (expiry.isSame(d)) {
                            return "Expiry";
                        } else {
                            return d.format("DD/MM/YY");
                        }
                    });
    }

    public updateVol(vol: number) {
        this.vol$.next(vol);
    }

    public updatePriceIncrement(increment: number) {
        this.priceIncrement$.next(increment);
    }

    public showPremium() {
        this.theoView$.next(false);
    }

    public showTheo() {
        this.theoView$.next(true);
    }
}
